import { useId } from "react"
import { AreaChart, Area, LineChart, Line, BarChart, Bar, ResponsiveContainer } from "recharts"

// Minimalist trend chart — no axes, no grid, no tooltip. Three variants:
//   variant="line" (default) — thin stroke only, for tight table cells
//   variant="area" — filled gradient under the line, for KPI tiles where
//                    the chart is the visual anchor of the card
//   variant="bar"  — thin vertical bars; used for KPI tiles whose metric
//                    is naturally a per-day count (e.g. idle breaches),
//                    where bars communicate "discrete daily volume"
//                    better than a smooth line.
type Props = {
  values: (number | null)[]
  height?: number
  /** Tailwind color class applied via currentColor — defaults to brand red. */
  colorClassName?: string
  strokeWidth?: number
  variant?: "line" | "area" | "bar"
  /** Hex color used for the filled area (variant="area" / "bar"). */
  fillColor?: string
}

export function Sparkline({
  values,
  height = 40,
  colorClassName = "text-brand-red",
  strokeWidth = 1.75,
  variant = "line",
  fillColor = "#ef4444",
}: Props) {
  const id = useId()
  if (values.length < 2) {
    return <div className="h-full rounded-control bg-white/[0.03]" style={{ height }} />
  }
  const data = values.map((v, i) => ({ i, v }))
  if (variant === "bar") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }} barCategoryGap={1}>
          <Bar dataKey="v" fill={fillColor} isAnimationActive={false} radius={[1, 1, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )
  }
  if (variant === "area") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <defs>
            <linearGradient id={`sparkfill-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={fillColor} stopOpacity={0.55} />
              <stop offset="100%" stopColor={fillColor} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={fillColor}
            strokeWidth={strokeWidth}
            fill={`url(#sparkfill-${id})`}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    )
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
        <Line
          type="monotone"
          dataKey="v"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          dot={false}
          isAnimationActive={false}
          className={colorClassName}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  )
}
